import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePermissions } from "@/hooks/usePermissions";
import {
  lawyerProfilesService,
  firmProfilesService,
  articlesService,
  teamPagesService,
} from "@/api/profiles";
import styles from "./ProfilesDashboard.module.css";

type StatCard = {
  label: string;
  value: number | null;
  loading: boolean;
  error: boolean;
};

export default function ProfilesDashboard() {
  const { hasPermission } = usePermissions();
  const [stats, setStats] = useState<StatCard[]>([
    { label: "Lawyer Profiles", value: null, loading: true, error: false },
    { label: "Firm Profiles", value: null, loading: true, error: false },
    { label: "Articles", value: null, loading: true, error: false },
    { label: "Team Pages", value: null, loading: true, error: false },
  ]);

  useEffect(() => {
    if (!hasPermission("profiles.read")) return;

    void (async () => {
      const results = await Promise.allSettled([
        lawyerProfilesService.list({ limit: 1, offset: 0 }),
        firmProfilesService.list({ limit: 1, offset: 0 }),
        articlesService.list({ limit: 1, offset: 0 }),
        teamPagesService.list({ limit: 1, offset: 0 }),
      ]);

      setStats([
        {
          label: "Lawyer Profiles",
          value: results[0].status === "fulfilled" ? results[0].value.total : null,
          loading: false,
          error: results[0].status === "rejected",
        },
        {
          label: "Firm Profiles",
          value: results[1].status === "fulfilled" ? results[1].value.total : null,
          loading: false,
          error: results[1].status === "rejected",
        },
        {
          label: "Articles",
          value: results[2].status === "fulfilled" ? results[2].value.total : null,
          loading: false,
          error: results[2].status === "rejected",
        },
        {
          label: "Team Pages",
          value: results[3].status === "fulfilled" ? results[3].value.total : null,
          loading: false,
          error: results[3].status === "rejected",
        },
      ]);
    })();
  }, [hasPermission]);

  if (!hasPermission("profiles.read")) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Profiles Editorial Dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Profiles — Editorial Dashboard"
        subtitle="Overview of all profiles portal content across firms"
      />

      <section className={styles.section} aria-labelledby="stats-heading">
        <h2 id="stats-heading" className={styles.sectionHeading}>
          Content Overview
        </h2>
        <div className={styles.statGrid}>
          {stats.map(stat => (
            <div key={stat.label} className={styles.statCard}>
              <span className={styles.statLabel}>{stat.label}</span>
              <span className={styles.statValue} aria-live="polite">
                {stat.loading ? (
                  <span className={styles.loading}>Loading...</span>
                ) : stat.error ? (
                  <span className={styles.statError}>Error</span>
                ) : (
                  (stat.value ?? 0).toLocaleString()
                )}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section} aria-labelledby="quicklinks-heading">
        <h2 id="quicklinks-heading" className={styles.sectionHeading}>
          Quick Links
        </h2>
        <nav className={styles.quickLinks} aria-label="Profiles portal quick links">
          <Link to="/profiles/firms" className={styles.quickLink}>
            <span className={styles.quickLinkTitle}>Firm Inspector</span>
            <span className={styles.quickLinkDesc}>
              Search and inspect a firm's profiles by company ID
            </span>
          </Link>
        </nav>
      </section>
    </div>
  );
}
